import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChefHat, Plus, Clock, CheckCircle2, Utensils, X, Trash2, Flame, PackageCheck, Bell, BellOff, SplitSquareHorizontal, Printer } from "lucide-react";

const STATUS = {
  new: { l: "جديد", c: "border-amber-400 bg-amber-50", bar: "bg-amber-500", Icon: Clock },
  cooking: { l: "قيد التحضير", c: "border-orange-400 bg-orange-50", bar: "bg-orange-500", Icon: Flame },
  ready: { l: "جاهز", c: "border-emerald-400 bg-emerald-50", bar: "bg-emerald-500", Icon: CheckCircle2 },
  served: { l: "مُقدَّم", c: "border-slate-300 bg-slate-50 opacity-70", bar: "bg-slate-400", Icon: PackageCheck },
};

const FLOW = { new: "cooking", cooking: "ready", ready: "served" };
const NEXT_LABEL = { new: "بدء التحضير", cooking: "جاهز للتقديم", ready: "تم التقديم" };

export default function KitchenDisplay() {
  const [orders, setOrders] = useState([]);
  const [tables, setTables] = useState([]);
  const [stations, setStations] = useState([]);
  const [stationFilter, setStationFilter] = useState("");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ table_id: "", waiter_name: "", notes: "", items: [{ name: "", qty: 1, notes: "" }] });
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("kds_sound") !== "0");
  const [splitDlg, setSplitDlg] = useState(null); // {order, groups: [{name, item_indices}]}
  const prevNewCountRef = React.useRef(-1);

  const playBell = React.useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const now = ctx.currentTime;
      [880, 660, 990].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + i * 0.18);
        gain.gain.linearRampToValueAtTime(0.25, now + i * 0.18 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.18 + 0.28);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.18); osc.stop(now + i * 0.18 + 0.3);
      });
      setTimeout(() => ctx.close(), 1200);
    } catch {}
  }, []);

  const load = async () => {
    try {
      const [o, t, s] = await Promise.all([
        api.get("/restaurant/orders", { params: stationFilter ? { station_id: stationFilter } : {} }),
        api.get("/restaurant/tables"),
        api.get("/restaurant/stations"),
      ]);
      const newCount = o.data.filter(x => x.status === "new").length;
      // Only ring after first load and when count increased
      if (soundOn && prevNewCountRef.current >= 0 && newCount > prevNewCountRef.current) playBell();
      prevNewCountRef.current = newCount;
      setOrders(o.data); setTables(t.data); setStations(s.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [soundOn, stationFilter]);

  const toggleSound = () => { const n = !soundOn; setSoundOn(n); localStorage.setItem("kds_sound", n ? "1" : "0"); if (n) playBell(); };

  const setStatus = async (o, status) => {
    try { await api.post(`/restaurant/orders/${o.id}/status`, { status }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (o) => {
    if (!window.confirm("حذف الطلب؟")) return;
    try { await api.delete(`/restaurant/orders/${o.id}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", qty: 1, notes: "" }] });
  const updItem = (i, patch) => { const it = [...form.items]; it[i] = { ...it[i], ...patch }; setForm({ ...form, items: it }); };
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const create = async () => {
    const clean = form.items.filter(it => it.name.trim());
    if (clean.length === 0) return toast.error("أضف صنفاً واحداً على الأقل");
    const tbl = tables.find(t => t.id === form.table_id);
    try {
      await api.post("/restaurant/orders", { ...form, items: clean, table_number: tbl?.number || "" });
      toast.success("تم إرسال الطلب للمطبخ");
      setDlg(false); setForm({ table_id: "", waiter_name: "", notes: "", items: [{ name: "", qty: 1, notes: "" }] });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const active = orders.filter(o => ["new", "cooking", "ready"].includes(o.status));
  const byStatus = { new: [], cooking: [], ready: [] };
  active.forEach(o => byStatus[o.status]?.push(o));

  const timeAgo = (iso) => {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}ث`;
    if (s < 3600) return `${Math.floor(s / 60)}د`;
    return `${Math.floor(s / 3600)}س`;
  };

  return (
    <div className="space-y-4" data-testid="kds-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><ChefHat className="w-6 h-6 text-primary" /> شاشة المطبخ</h1>
          <p className="text-sm text-muted-foreground mt-1">تتبع الطلبات في الوقت الفعلي — تحديث تلقائي كل 20 ثانية</p>
        </div>
        <div className="flex gap-2 items-center">
          {stations.length > 0 && (
            <select value={stationFilter} onChange={(e) => setStationFilter(e.target.value)} className="h-9 px-2 border rounded bg-background text-sm" data-testid="kds-station-filter">
              <option value="">كل الأقسام</option>
              {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <Button onClick={toggleSound} variant="outline" size="icon" title={soundOn ? "كتم الجرس" : "تشغيل الجرس"} data-testid="sound-toggle" className={soundOn ? "text-emerald-600 border-emerald-300" : "text-slate-400"}>
            {soundOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
          </Button>
          <Button onClick={() => setDlg(true)} className="gap-2" data-testid="new-order-btn"><Plus className="w-4 h-4" /> طلب جديد</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {["new", "cooking", "ready"].map(st => {
          const s = STATUS[st]; const SIcon = s.Icon;
          return (
            <div key={st} className="space-y-2" data-testid={`col-${st}`}>
              <div className={`h-1 rounded-full ${s.bar}`}></div>
              <div className="flex items-center justify-between px-1">
                <div className="font-bold text-sm flex items-center gap-1.5"><SIcon className="w-4 h-4" /> {s.l}</div>
                <div className="text-xs text-muted-foreground">{byStatus[st].length}</div>
              </div>
              {byStatus[st].length === 0 && <div className="text-center text-xs text-muted-foreground py-6">—</div>}
              {byStatus[st].map(o => (
                <div key={o.id} className={`p-3 rounded-lg border-2 ${s.c}`} data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-bold text-sm">{o.table_number ? `طاولة ${o.table_number}` : (o.order_type === "takeaway" ? "سفري" : o.order_type === "delivery" ? "توصيل" : "بدون طاولة")}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{timeAgo(o.created_at)}</div>
                  </div>
                  {o.station_name && <div className="inline-block text-[10px] font-bold px-2 py-0.5 rounded bg-primary/10 text-primary mb-2" data-testid={`order-station-${o.id}`}>{o.station_name}</div>}
                  {o.waiter_name && <div className="text-[10px] text-muted-foreground mb-2">النادل: {o.waiter_name}</div>}
                  <div className="space-y-1 mb-2">
                    {(o.items || []).map((it, i) => (
                      <div key={i} className="text-xs flex items-start gap-2">
                        <span className="font-bold min-w-[24px]">{it.qty}×</span>
                        <div className="flex-1">
                          <div>{it.name}</div>
                          {it.notes && <div className="text-[10px] text-orange-700 italic">↳ {it.notes}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {o.notes && <div className="text-[10px] text-muted-foreground bg-white/50 p-1 rounded mb-2">{o.notes}</div>}
                  <div className="flex gap-1 flex-wrap">
                    {FLOW[o.status] && (
                      <Button size="sm" className="flex-1 h-7 text-xs" onClick={() => setStatus(o, FLOW[o.status])} data-testid={`advance-${o.id}`}>
                        {NEXT_LABEL[o.status]}
                      </Button>
                    )}
                    {o.status === "ready" && (
                      <Button size="icon" variant="outline" className="h-7 w-7" title="قسّم الحساب" onClick={() => setSplitDlg({ order: o, groups: [{ name: "زبون 1", item_indices: [] }, { name: "زبون 2", item_indices: [] }] })} data-testid={`split-${o.id}`}><SplitSquareHorizontal className="w-3 h-3" /></Button>
                    )}
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setStatus(o, "cancelled")} title="إلغاء"><X className="w-3 h-3" /></Button>
                    <Button size="icon" variant="outline" className="h-7 w-7 text-red-500" onClick={() => del(o)} title="حذف"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg" data-testid="new-order-dialog">
          <DialogHeader><DialogTitle>طلب جديد</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>الطاولة</Label>
                <select className="w-full h-10 px-2 border rounded bg-background text-sm" value={form.table_id} onChange={e => setForm({ ...form, table_id: e.target.value })} data-testid="order-table">
                  <option value="">— بدون —</option>
                  {tables.map(t => <option key={t.id} value={t.id}>طاولة {t.number} ({t.capacity})</option>)}
                </select>
              </div>
              <div><Label>النادل</Label><Input value={form.waiter_name} onChange={e => setForm({ ...form, waiter_name: e.target.value })} placeholder="اسم النادل" /></div>
            </div>
            <div>
              <Label>الأصناف</Label>
              <div className="space-y-1.5">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[80px_1fr_auto] gap-1.5">
                    <Input type="number" value={it.qty} onChange={e => updItem(i, { qty: parseFloat(e.target.value) || 1 })} className="text-center" />
                    <div className="space-y-1">
                      <Input value={it.name} onChange={e => updItem(i, { name: e.target.value })} placeholder="اسم الصنف" data-testid={`item-name-${i}`} />
                      <Input value={it.notes || ""} onChange={e => updItem(i, { notes: e.target.value })} placeholder="ملاحظة (بدون بصل...)" className="text-xs h-7" />
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => rmItem(i)} className="h-9 w-9 text-red-500" disabled={form.items.length === 1}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={addItem} className="mt-2 h-7 text-xs" data-testid="add-item-btn"><Plus className="w-3 h-3 me-1" /> صنف آخر</Button>
            </div>
            <div><Label>ملاحظات عامة</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button><Button onClick={create} data-testid="send-order-btn"><Utensils className="w-4 h-4 me-1" /> إرسال للمطبخ</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <SplitBillDialog open={!!splitDlg} state={splitDlg} setState={setSplitDlg} />
    </div>
  );
}

// Split bill sub-component
function SplitBillDialog({ open, state, setState }) {
  const [preview, setPreview] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => { if (!open) setPreview(null); }, [open]);

  if (!state) return null;
  const { order, groups } = state;
  const items = order?.items || [];

  const setGroups = (gs) => setState({ ...state, groups: gs });
  const addGroup = () => setGroups([...groups, { name: `زبون ${groups.length + 1}`, item_indices: [] }]);
  const rmGroup = (i) => setGroups(groups.filter((_, x) => x !== i));
  const renameGroup = (i, v) => setGroups(groups.map((g, x) => x === i ? { ...g, name: v } : g));
  const toggleItem = (gIdx, itemIdx) => {
    // remove from all groups first, then add to selected
    const cleaned = groups.map(g => ({ ...g, item_indices: g.item_indices.filter(i => i !== itemIdx) }));
    cleaned[gIdx].item_indices.push(itemIdx);
    setGroups(cleaned);
  };
  const unassignItem = (itemIdx) => setGroups(groups.map(g => ({ ...g, item_indices: g.item_indices.filter(i => i !== itemIdx) })));
  const groupOf = (itemIdx) => groups.findIndex(g => g.item_indices.includes(itemIdx));

  const computePreview = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/restaurant/orders/${order.id}/split-preview`, { groups });
      setPreview(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  const savePrint = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/restaurant/orders/${order.id}/split-save`, { groups });
      setPreview(data);
      // Print
      const win = window.open("", "_blank");
      if (win) {
        const rows = (grp) => (grp.items || []).map(it => `<tr><td>${it.name}${it.notes ? ` <small>(${it.notes})</small>` : ""}</td><td style="text-align:center">${it.qty}</td><td style="text-align:end">${(it.unit_price||0).toLocaleString("ar-EG")}</td><td style="text-align:end">${(it.line_total||0).toLocaleString("ar-EG")}</td></tr>`).join("");
        win.document.write(`<html dir="rtl"><head><title>فواتير مقسّمة</title><style>body{font-family:sans-serif;padding:20px;max-width:800px;margin:auto}table{width:100%;border-collapse:collapse;margin:8px 0}th,td{border-bottom:1px solid #ddd;padding:6px;font-size:12px}h2{margin:20px 0 4px;padding:6px 10px;background:#f1f5f9;border-radius:4px}.tot{font-weight:bold;text-align:end;margin-top:4px;font-size:14px}</style></head><body>
        <h1 style="text-align:center">تقسيم فاتورة — طاولة ${order.table_number || "—"}</h1>
        ${data.groups.map(g => `<h2>${g.name}</h2><table><thead><tr><th>الصنف</th><th>الكمية</th><th>السعر</th><th>المجموع</th></tr></thead><tbody>${rows(g)}</tbody></table><div class="tot">إجمالي: ${g.subtotal.toLocaleString("ar-EG")}</div>`).join("")}
        ${data.shared.items.length ? `<h2>مشترك</h2><table><thead><tr><th>الصنف</th><th>الكمية</th><th>السعر</th><th>المجموع</th></tr></thead><tbody>${rows(data.shared)}</tbody></table><div class="tot">مشترك: ${data.shared.subtotal.toLocaleString("ar-EG")}</div>` : ""}
        <hr/><div class="tot" style="font-size:18px">الإجمالي الكلي: ${data.total.toLocaleString("ar-EG")}</div>
        </body></html>`);
        win.document.close(); setTimeout(() => win.print(), 400);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && setState(null)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="split-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><SplitSquareHorizontal className="w-5 h-5 text-primary" /> تقسيم حساب طاولة {order?.table_number || "—"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Groups */}
          <div className="grid gap-2 md:grid-cols-2">
            {groups.map((g, gi) => (
              <div key={gi} className="p-3 rounded border bg-primary/5" data-testid={`group-${gi}`}>
                <div className="flex items-center gap-1 mb-2">
                  <Input value={g.name} onChange={e => renameGroup(gi, e.target.value)} className="h-7 text-sm font-bold" />
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => rmGroup(gi)} disabled={groups.length <= 1}><X className="w-3.5 h-3.5" /></Button>
                </div>
                {g.item_indices.length === 0 ? (
                  <div className="text-[10px] text-muted-foreground italic">لم يُضف أي صنف — انقر على الأصناف أدناه ثم اختر هذه المجموعة</div>
                ) : (
                  <div className="space-y-1">
                    {g.item_indices.map(idx => (
                      <div key={idx} className="text-xs bg-background rounded p-1.5 flex justify-between">
                        <span>{items[idx]?.qty}× {items[idx]?.name}</span>
                        <button onClick={() => unassignItem(idx)} className="text-red-500 text-[10px]">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={addGroup} data-testid="add-group-btn"><Plus className="w-3.5 h-3.5 me-1" /> مجموعة جديدة</Button>

          {/* Items to assign */}
          <div>
            <div className="text-xs font-bold mb-2">أصناف الطلب — انقر على الصنف واختر لأي مجموعة يذهب</div>
            <div className="space-y-1.5">
              {items.map((it, idx) => {
                const gIdx = groupOf(idx);
                return (
                  <div key={idx} className="flex items-center gap-2 p-2 rounded border" data-testid={`item-row-${idx}`}>
                    <span className="flex-1 text-sm">{it.qty}× {it.name}{it.notes && <span className="text-[10px] text-orange-600 ms-2">({it.notes})</span>}</span>
                    <div className="flex gap-1 flex-wrap">
                      {groups.map((g, gi) => (
                        <button key={gi} onClick={() => toggleItem(gi, idx)} className={`text-[10px] px-2 py-0.5 rounded ${gIdx === gi ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted-foreground/10"}`} data-testid={`assign-${idx}-${gi}`}>{g.name}</button>
                      ))}
                      {gIdx >= 0 && <button onClick={() => unassignItem(idx)} className="text-[10px] px-1 text-red-500">✕</button>}
                      {gIdx < 0 && <span className="text-[10px] text-muted-foreground px-2 py-0.5">مشترك</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="p-3 rounded border-2 border-emerald-300 bg-emerald-50 space-y-2" data-testid="split-preview">
              <div className="font-bold text-emerald-800 text-sm">معاينة</div>
              {preview.groups.map((g, i) => (
                <div key={i} className="text-xs flex justify-between bg-background rounded p-2">
                  <span>{g.name} ({g.count} أصناف)</span>
                  <b className="font-mono">{g.subtotal.toLocaleString("ar-EG")}</b>
                </div>
              ))}
              {preview.shared?.items?.length > 0 && (
                <div className="text-xs flex justify-between bg-background rounded p-2">
                  <span>مشترك ({preview.shared.items.length})</span>
                  <b className="font-mono">{preview.shared.subtotal.toLocaleString("ar-EG")}</b>
                </div>
              )}
              <div className="text-sm flex justify-between font-bold border-t pt-2 mt-2">
                <span>الإجمالي</span><span className="font-mono text-primary">{preview.total.toLocaleString("ar-EG")}</span>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setState(null)}>إغلاق</Button>
          <Button variant="outline" onClick={computePreview} disabled={saving} data-testid="preview-btn">معاينة</Button>
          <Button onClick={savePrint} disabled={saving} data-testid="save-split-btn"><Printer className="w-4 h-4 me-1" /> حفظ وطباعة</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
